import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BookOpen, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// City slug → list of preferred blog slugs (most-relevant first). Pulled from
// the V3 long-form set so the related strip naturally pushes deep tuition-
// intent content into the city reader flow.
const CITY_BLOG_MAP = {
    jabalpur: [
        "best-home-tuition-jabalpur-cbse-students-2026",
        "home-tuition-vs-coaching-classes-jabalpur-comparison",
        "home-tuition-for-weak-students-jabalpur-recovery-plan",
        "cbse-vs-icse-home-tuition-jabalpur-which-board",
        "best-home-tuition-jabalpur-2026",
        "icse-home-tuition-jabalpur",
    ],
    bhopal: [
        "why-bhopal-parents-prefer-1-on-1-home-tutors",
        "best-female-home-tutors-bhopal-verified",
        "home-tutor-cost-bhopal-2026-fee-breakdown",
        "10-benefits-of-one-on-one-home-tuition-real-outcomes",
    ],
    nagpur: [
        "top-neet-home-tutors-nagpur-how-to-hire",
        "how-to-find-verified-home-tutor-nagpur-2026",
        "jee-preparation-at-home-personal-tutors-mp-maharashtra",
        "10-benefits-of-one-on-one-home-tuition-real-outcomes",
    ],
};

function BlogCard({ blog }) {
    return (
        <Link
            to={`/post/${blog.slug}`}
            data-testid={`city-related-blog-${blog.slug}`}
            className="group flex flex-col h-full bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#1E3A8A]/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
        >
            {blog.cover_image && (
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img
                        src={blog.cover_image}
                        alt={blog.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                </div>
            )}
            <div className="flex-1 p-5 flex flex-col">
                {blog.category && (
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#1E3A8A]/5 text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A] mb-2 self-start">
                        <BookOpen className="w-3 h-3" /> {blog.category}
                    </div>
                )}
                <h3 className="font-heading font-bold text-[#0F172A] text-base leading-snug mb-2 line-clamp-2">
                    {blog.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">
                    {blog.excerpt}
                </p>
                <div className="mt-auto inline-flex items-center gap-1 text-[13px] font-semibold text-[#1E3A8A] group-hover:gap-2 transition-all">
                    Read article <ArrowRight className="w-3.5 h-3.5" />
                </div>
            </div>
        </Link>
    );
}

export default function RelatedCityBlogs({ citySlug, cityName }) {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const preferred = CITY_BLOG_MAP[citySlug] || [];

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const res = await fetch(`${API}/blogs`);
                if (!res.ok) throw new Error("blogs fetch failed");
                const all = await res.json();
                // Pick the preferred slugs in order, drop any that aren't published yet.
                const map = new Map(all.map((b) => [b.slug, b]));
                const ordered = preferred
                    .map((s) => map.get(s))
                    .filter(Boolean)
                    .slice(0, 3);
                if (!cancelled) setBlogs(ordered);
            } catch {
                if (!cancelled) setBlogs([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [citySlug]);

    if (loading || blogs.length === 0) return null;

    return (
        <section data-testid={`city-related-blogs-${citySlug}`} className="py-16 sm:py-20 bg-white border-t border-slate-100">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">
                        <BookOpen className="w-3 h-3" /> Read before you decide
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A]">
                        Helpful reads for {cityName} parents
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
                        Honest, in-depth guides from our editorial team — fees, tutor selection, comparison and recovery plans for {cityName} families.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {blogs.map((b) => (
                        <BlogCard key={b.slug} blog={b} />
                    ))}
                </div>

                <div className="text-center mt-10">
                    <Link
                        to="/blog"
                        data-testid="city-related-blogs-all-cta"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors text-sm"
                    >
                        Browse all articles <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
